import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Grid,
  Heading,
  Text,
  Stat,
  StatHelpText,
  StatArrow,
  Icon,
  Card,
  CardHeader,
  CardBody,
  Stack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  HStack,
  Button,
  Select,
  useColorModeValue,
  Spinner,
  Divider,
  Avatar,
  AvatarGroup,
} from '@chakra-ui/react';
import {
  FiUsers,
  FiAlertCircle,
  FiActivity,
  FiCheckCircle,
  FiX,
  FiClock,
  FiList,
  FiLayers,
  FiRefreshCw,
  FiUserCheck,
  FiMessageSquare,
  FiCreditCard,
} from 'react-icons/fi';
import { MdAdminPanelSettings, MdSupervisorAccount, MdPeople } from 'react-icons/md';

// Reusable components
const StatCard = ({ title, value, icon, change, colorScheme, formatter, isLoading, footerText }) => {
  const cardBg = useColorModeValue('white', 'gray.700');
  const iconBg = useColorModeValue(`${colorScheme}.500`, `${colorScheme}.200`);
  const iconColor = useColorModeValue('white', 'gray.800');

  const displayValue = formatter ? formatter(value) : value;

  return (
    <Card boxShadow="md" bg={cardBg}>
      <CardBody p={4}>
        <Flex alignItems="center">
          <Box
            bg={iconBg}
            color={iconColor}
            p={3}
            borderRadius="lg"
            mr={4}
            boxShadow="md"
          >
            <Icon as={icon} boxSize={6} />
          </Box>
          <Box flex="1">
            <Text fontSize="sm" color="gray.500" fontWeight="medium">
              {title}
            </Text>
            {isLoading ? (
              <Spinner size="sm" mt={1} colorScheme={colorScheme} />
            ) : (
              <Text fontSize="2xl" fontWeight="bold">
                {displayValue}
              </Text>
            )}
            {change && (
              <Stat>
                <StatHelpText mb={0}>
                  <StatArrow type={change > 0 ? 'increase' : 'decrease'} />
                  {Math.abs(change)}%
                </StatHelpText>
              </Stat>
            )}
            {footerText && (
              <Text fontSize="xs" color="gray.500" mt={1}>
                {footerText}
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};

// Main Dashboard Component
const AdministracaoDashboard = () => {
  const [stats, setStats] = useState({
    usersTotal: { value: 0, change: 0 },
    activeUsers: { value: 0, change: 0 },
    pendingRequests: { value: 0, change: 0 },
    disputes: { value: 0, change: 0 },
    cancelations: { value: 0, change: 0 },
    admins: { value: 0, change: 0 },
    supportTickets: { value: 0, change: 0 },
  });
  const [timeFrame, setTimeFrame] = useState('month');
  const [isLoading, setIsLoading] = useState(true);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    // Simulating API call
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // In a real implementation, this would be an API call
        // const response = await api.get('api/dashboard/administracao');
        // const data = await response.json();
        // setStats(data.stats);
        
        // Mock data for demo
        setTimeout(() => {
          setStats({
            usersTotal: { value: 7835, change: 12 },
            activeUsers: { value: 5248, change: 5 },
            pendingRequests: { value: 43, change: -8 },
            disputes: { value: 17, change: -15 },
            cancelations: { value: 56, change: -5 },
            admins: { value: 42, change: 2 },
            supportTickets: { value: 28, change: -20 },
          });
          
          // Mock pending approvals
          setPendingApprovals([
            { id: 1, type: 'Verificação', name: 'João Silva', status: 'Aguardando', time: '2h atrás', priority: 'Alta' },
            { id: 2, type: 'Categoria', name: 'Instalação de Aquecedores', status: 'Aguardando', time: '5h atrás', priority: 'Média' },
            { id: 3, type: 'Disputa', name: 'Serviço #4562', status: 'Em análise', time: '8h atrás', priority: 'Alta' },
            { id: 4, type: 'Verificação', name: 'Maria Souza', status: 'Aguardando', time: '1d atrás', priority: 'Baixa' },
            { id: 5, type: 'Categoria', name: 'Pintura Residencial', status: 'Em análise', time: '1d atrás', priority: 'Média' },
          ]);
          
          // Mock activity
          setRecentActivity([
            { id: 1, action: 'Aprovação', description: 'Novo prestador aprovado', user: 'Carlos Ferreira', time: '30min atrás' },
            { id: 2, action: 'Rejeição', description: 'Solicitação de categoria rejeitada', user: 'Ana Costa', time: '1h atrás' },
            { id: 3, action: 'Disputa', description: 'Disputa resolvida a favor do cliente', user: 'Roberto Santos', time: '3h atrás' },
            { id: 4, action: 'Sistema', description: 'Backup do sistema realizado', user: 'Sistema', time: '5h atrás' },
            { id: 5, action: 'Financeiro', description: 'Pagamentos do dia processados', user: 'Sistema', time: '8h atrás' },
          ]);
          
          setIsLoading(false);
        }, 1500);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
  }, [timeFrame]);

  const handleTimeFrameChange = (e) => {
    setTimeFrame(e.target.value);
  };

  const cardBg = useColorModeValue('white', 'gray.700');
  const tableBg = useColorModeValue('gray.50', 'gray.700');

  // Status badge colorScheme mapper
  const getStatusColor = (status) => {
    switch (status) {
      case 'Aguardando':
        return 'yellow';
      case 'Em análise':
        return 'blue';
      case 'Aprovado':
        return 'green';
      case 'Rejeitado':
        return 'red';
      default:
        return 'gray';
    }
  };

  // Priority badge colorScheme mapper
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Alta':
        return 'red';
      case 'Média':
        return 'orange';
      case 'Baixa':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Action badge colorScheme mapper
  const getActionColor = (action) => {
    switch (action) {
      case 'Aprovação':
        return 'green';
      case 'Rejeição':
        return 'red';
      case 'Disputa':
        return 'purple';
      case 'Sistema':
        return 'blue';
      case 'Financeiro':
        return 'teal';
      default:
        return 'gray';
    }
  };

  return (
    <Box w="100%" p={4}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg">Dashboard Administrativo</Heading>
          <Text color="gray.500">Gestão de usuários e operações do sistema</Text>
        </Box>
        <HStack>
          <Text mr={2}>Período:</Text>
          <Select value={timeFrame} onChange={handleTimeFrameChange} w="150px">
            <option value="day">Hoje</option>
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Ano</option>
          </Select>
          <Button 
            leftIcon={<FiRefreshCw />} 
            colorScheme="yellow" 
            variant="outline"
            onClick={() => {
              setIsLoading(true);
              setTimeout(() => setIsLoading(false), 1000);
            }}
          >
            Atualizar
          </Button>
        </HStack>
      </Flex>

      {/* Main Stats */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Total de Usuários" 
          value={stats.usersTotal.value} 
          icon={FiUsers} 
          change={stats.usersTotal.change} 
          colorScheme="blue" 
          isLoading={isLoading} 
          footerText="Contratantes e contratados"
        />
        <StatCard 
          title="Usuários Ativos" 
          value={stats.activeUsers.value} 
          icon={FiUserCheck} 
          change={stats.activeUsers.change} 
          colorScheme="green" 
          isLoading={isLoading}
          footerText="Usuários com atividade recente"
        />
        <StatCard 
          title="Solicitações Pendentes" 
          value={stats.pendingRequests.value} 
          icon={FiClock} 
          change={stats.pendingRequests.change} 
          colorScheme="yellow" 
          isLoading={isLoading}
          footerText="Aguardando análise"
        />
        <StatCard 
          title="Disputas Abertas" 
          value={stats.disputes.value} 
          icon={FiAlertCircle} 
          change={stats.disputes.change} 
          colorScheme="red" 
          isLoading={isLoading}
          footerText="Conflitos entre usuários"
        />
      </Grid>

      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "repeat(3, 1fr)" }} gap={4} mb={6}>
        <StatCard 
          title="Cancelamentos" 
          value={stats.cancelations.value} 
          icon={FiX} 
          change={stats.cancelations.change} 
          colorScheme="red" 
          isLoading={isLoading}
          footerText="Serviços cancelados"
        />
        <StatCard 
          title="Administradores Ativos" 
          value={stats.admins.value} 
          icon={MdAdminPanelSettings} 
          change={stats.admins.change} 
          colorScheme="purple" 
          isLoading={isLoading}
          footerText="Equipe administrativa"
        />
        <StatCard 
          title="Tickets de Suporte" 
          value={stats.supportTickets.value} 
          icon={FiMessageSquare} 
          change={stats.supportTickets.change} 
          colorScheme="teal" 
          isLoading={isLoading}
          footerText="Atendimentos em aberto"
        />
      </Grid>

      {/* Pending Approvals Table */}
      <Card bg={cardBg} boxShadow="md" mb={6}>
        <CardHeader pb={0}>
          <Flex justify="space-between" align="center">
            <Heading size="md">Aprovações Pendentes</Heading>
            <Badge colorScheme="yellow" fontSize="sm" borderRadius="full" px={3} py={1}>
              {isLoading ? '-' : pendingApprovals.length} itens
            </Badge>
          </Flex>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Flex justify="center" py={6}>
              <Spinner />
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple" size="sm">
                <Thead bg={tableBg}>
                  <Tr>
                    <Th>ID</Th>
                    <Th>Tipo</Th>
                    <Th>Nome</Th>
                    <Th>Status</Th>
                    <Th>Tempo</Th>
                    <Th>Prioridade</Th>
                    <Th>Ações</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pendingApprovals.map(item => (
                    <Tr key={item.id}>
                      <Td>#{item.id}</Td>
                      <Td>{item.type}</Td>
                      <Td>{item.name}</Td>
                      <Td>
                        <Badge colorScheme={getStatusColor(item.status)}>
                          {item.status}
                        </Badge>
                      </Td>
                      <Td>{item.time}</Td>
                      <Td>
                        <Badge colorScheme={getPriorityColor(item.priority)} variant="outline">
                          {item.priority}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={2}>
                          <Button size="xs" colorScheme="green" leftIcon={<FiCheckCircle size="12px" />}>
                            Aprovar
                          </Button>
                          <Button size="xs" colorScheme="red" variant="outline" leftIcon={<FiX size="12px" />}>
                            Rejeitar
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
          <Flex justify="center" mt={4}>
            <Button size="sm" colorScheme="yellow" variant="outline">
              Ver todos
            </Button>
          </Flex>
        </CardBody>
      </Card>

      {/* Admin Team & Activity Feed */}
      <Grid templateColumns={{ base: "repeat(1, 1fr)", lg: "1fr 2fr" }} gap={6}>
        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={0}>
            <Heading size="md">Equipe Administrativa</Heading>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={4} divider={<Divider />}>
                <Box>
                  <Text fontWeight="medium" mb={2}>Administradores Online</Text>
                  <AvatarGroup size="md" max={5}>
                    <Avatar name="Carlos Silva" bg="blue.500" />
                    <Avatar name="Ana Costa" bg="green.500" />
                    <Avatar name="Roberto Santos" bg="yellow.500" />
                    <Avatar name="Maria Oliveira" bg="red.500" />
                    <Avatar name="Fernando Lima" bg="purple.500" />
                    <Avatar name="Juliana Martins" bg="teal.500" />
                  </AvatarGroup>
                  <Text fontSize="sm" mt={2} color="gray.500">6 de 42 administradores online</Text>
                </Box>

                <Box>
                  <Text fontWeight="medium" mb={2}>Funções Ativas</Text>
                  <Stack spacing={2}>
                    <Flex justify="space-between">
                      <HStack>
                        <Icon as={MdSupervisorAccount} color="blue.500" />
                        <Text fontSize="sm">Suporte</Text>
                      </HStack>
                      <Text fontSize="sm">12 admins</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <HStack>
                        <Icon as={FiCreditCard} color="green.500" />
                        <Text fontSize="sm">Financeiro</Text>
                      </HStack>
                      <Text fontSize="sm">8 admins</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <HStack>
                        <Icon as={FiLayers} color="purple.500" />
                        <Text fontSize="sm">Moderação</Text>
                      </HStack>
                      <Text fontSize="sm">15 admins</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <HStack>
                        <Icon as={MdPeople} color="yellow.500" />
                        <Text fontSize="sm">Verificação</Text>
                      </HStack>
                      <Text fontSize="sm">7 admins</Text>
                    </Flex>
                  </Stack>
                </Box>

                <Box>
                  <Text fontWeight="medium" mb={2}>Métricas de Desempenho</Text>
                  <Stack spacing={2}>
                    <Flex justify="space-between">
                      <Text fontSize="sm">Tempo Médio de Resposta</Text>
                      <Text fontSize="sm" fontWeight="bold">1.8h</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontSize="sm">SLA de Atendimento</Text>
                      <Text fontSize="sm" fontWeight="bold">97.3%</Text>
                    </Flex>
                    <Flex justify="space-between">
                      <Text fontSize="sm">Resolução no Primeiro Contato</Text>
                      <Text fontSize="sm" fontWeight="bold">82.1%</Text>
                    </Flex>
                  </Stack>
                </Box>
              </Stack>
            )}
          </CardBody>
        </Card>

        <Card bg={cardBg} boxShadow="md">
          <CardHeader pb={0}>
            <Flex align="center">
              <Icon as={FiActivity} mr={2} />
              <Heading size="md">Atividades Administrativas</Heading>
            </Flex>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <Flex justify="center" py={6}>
                <Spinner />
              </Flex>
            ) : (
              <Stack spacing={3}>
                {recentActivity.map(activity => (
                  <Flex 
                    key={activity.id}
                    justify="space-between" 
                    p={3} 
                    bg="gray.50" 
                    borderRadius="md"
                    borderLeft="4px solid" 
                    borderLeftColor={`${getActionColor(activity.action)}.500`}
                  >
                    <Box flex="1">
                      <Flex align="center" mb={1}>
                        <Badge colorScheme={getActionColor(activity.action)} mr={2}>
                          {activity.action}
                        </Badge>
                        <Text fontWeight="medium">{activity.description}</Text>
                      </Flex>
                      <Flex justify="space-between" fontSize="sm" color="gray.500">
                        <Text>Por: {activity.user}</Text>
                        <Text>{activity.time}</Text>
                      </Flex>
                    </Box>
                  </Flex>
                ))}
                <Flex justify="center" mt={2}>
                  <Button size="sm" leftIcon={<FiList />} colorScheme="yellow" variant="outline">
                    Ver histórico completo
                  </Button>
                </Flex>
              </Stack>
            )}
          </CardBody>
        </Card>
      </Grid>
    </Box>
  );
};

export default AdministracaoDashboard;